<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { getAuth } from 'firebase/auth'
import { database } from '@/firebase/config'
import { ref as dbRef, onValue, off, update, query, orderByChild, startAt, endAt, limitToFirst, get, type Query } from 'firebase/database'
import FileUploader from '@/components/FileUploader.vue'
import CertificatesModal from '@/components/certificados/CertificatesModal.vue'
import PackageViewCertificate from '@/components/certificados/PackageViewCertificate.vue'
import { ICertificatePackage, ICertificate, ICertificateSearchResult, TSearchType } from '@shared/types'
import NewCertificateModal from '@/components/certificados/NewCertificateModal.vue'
import { certificateService } from '@/services/certificateService'
import CertificatesView from '@/components/certificados/CertificatesView.vue'
import PackageModal from '@/components/certificados/PackageModal.vue'
import CertificateDetailsModal from '@/components/certificados/CertificateDetailsModal.vue'
import CleanErrorModal from '@/components/common/CleanErrorModal.vue'
import BaseModal from '@/components/BaseModal.vue'
import { es } from 'date-fns/locale/es'
import { VueDatePicker } from '@vuepic/vue-datepicker'

type TCertificate = ICertificate & {
	selected: boolean
}

const store = useStore()
const auth = getAuth()
const db = database

const carreras = computed(() => store.state.global.selectedInstitution.Carrera)
const activeTab = ref('certificates')
const certificatesList = ref<TCertificate[]>([])
const packages = ref<ICertificatePackage[]>([])
const loading = ref(false)
const showNewCertificateModal = ref(false)
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
const testMode = computed(() => store.getters.isTestMode)
const currentInstitution = computed(() => store.getters['global/currentInstitution'])
const showModalDownloadTemplate = ref(false)
const showPackageModal = ref(false)
const selectedCertificates = ref<TCertificate[]>([])
const showCertificate = ref(false)
const selectedCertificateForDetail = ref<ICertificate | null>(null)
const onCertificateDetailModalUpdate = (value: boolean) => {
	showCertificate.value = value
	if (!value) selectedCertificateForDetail.value = null
}
const openCertificateDetail = (certificate: ICertificate) => {
	selectedCertificateForDetail.value = certificate
	showCertificate.value = true
}
const showCleanErrorModal = ref(false)
const errorCount = ref(0)
const packagesDate = ref(new Date().getFullYear())
let _packagesListenerQuery: Query | null = null
let cleanupUploadStatusListener: (() => void) | null = null
// Computed properties for selected certificates
const selectedCertificatesList = computed(() => certificatesList.value.filter(c => c.selected))

// Firebase paths
const getInstitutionPath = (path: string) => `institutions/${currentInstitution.value}${testMode.value ? '/testMode' : ''}${path}`

const getUserPath = (path: string) => `users/${auth.currentUser?.uid}${path}`

const formateDatePicker = (year: number) => {
	const month = 1 // 1-12
	const day = 1
	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}
// Search options
const searchValue = ref('')
const singleSelect = ref({ value: 'folio', text: 'Folio de control', icon: 'tag' })
const searchResult = ref<ICertificateSearchResult[]>([])

const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/
function getYearRange(dateStr: string): { start: number; end: number } {
	const trimmed = dateStr?.trim?.() ?? ''
	if (!DATE_FORMAT.test(trimmed)) {
		throw new TypeError('date debe tener formato YYYY-MM-DD')
	}
	const year = Number.parseInt(trimmed.slice(0, 4), 10)
	const startDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0))
	const endDate = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999))
	return { start: startDate.getTime(), end: endDate.getTime() }
}
// Tab selection
const selectTab = async (tab: string) => {
	if (activeTab.value === tab) return

	// Limpiar listeners de la pestaña anterior
	if (activeTab.value === 'certificates') {
		const certificatesRef = dbRef(db, getInstitutionPath('/certificates/queue'))
		off(certificatesRef)
	} else if (activeTab.value === 'packages') {
		if (_packagesListenerQuery) {
			off(_packagesListenerQuery)
			_packagesListenerQuery = null
		} else {
			const packagesRef = dbRef(db, getInstitutionPath('/certificates/packages'))
			off(packagesRef)
		}
	}

	activeTab.value = tab

	if (tab === 'certificates') {
		await loadCertificates(true)
	} else if (tab === 'packages') {
		await loadPackages(formateDatePicker(packagesDate.value))
	}
}

// Marcar processing en RTDB al iniciar la subida a Storage (antes del trigger del backend)
const handleUploadStart = async (startInfo: { fileName: string; path: string }) => {
	try {
		const uploadRef = dbRef(db, getUserPath('/userData/uploadCertificate/'))
		await update(uploadRef, {
			status: 'uploading',
			errorsMessages: null,
			initTime: new Date().getTime(),
			filePath: startInfo.path,
			fileName: startInfo.fileName
		})
	} catch (error) {
		console.error('Error al iniciar estado de subida:', error)
		if (error instanceof Error) {
			store.dispatch('loader/setError', error.message)
		}
	}
}

// Handle upload complete from FileUploader
const handleUploadComplete = async (uploadResult: { url: string; fileName: string; fileType: string; path: string }) => {
	try {
		const uploadRef = dbRef(db, getUserPath('/userData/uploadCertificate/'))
		const snapshot = await get(uploadRef)
		const current = snapshot.val()

		// Evitar race: el backend puede haber terminado (remove) o reportado error antes de este callback
		if (!current || current.status === 'error' || current.status === 'timeout' || current.status === 'complete') {
			await selectTab('certificates')
			return
		}

		// Backend aún no terminó: refrescar initTime desde que el archivo ya está en Storage
		await update(uploadRef, {
			status: 'uploading',
			errorsMessages: null,
			initTime: new Date().getTime(),
			filePath: uploadResult.path,
			fileName: uploadResult.fileName
		})

		await selectTab('certificates')
	} catch (error) {
		console.error('Error al procesar la subida:', error)
		if (error instanceof Error) {
			store.dispatch('loader/setError', error.message)
		}
	}
}

// Handle upload error from FileUploader
const handleUploadError = async (error: Error) => {
	console.error('Error en la subida:', error)
	store.dispatch('loader/setError', error.message)
	try {
		const uploadRef = dbRef(db, getUserPath('/userData/uploadCertificate/'))
		await update(uploadRef, {
			status: 'error',
			errorsMessages: [{ message: error.message || 'Error al subir el archivo' }]
		})
	} catch (updateError) {
		console.error('Error al actualizar estado de subida tras fallo:', updateError)
	}
}

// Handle upload progress from FileUploader
const handleUploadProgress = (progress: number) => {
	store.dispatch('loader/updateProgress', progress)
}

// Variables para mostrar progreso simple
const totalCertificates = ref(0) // Total de certificados
const processedCertificates = ref(0) // Certificados procesados
const processingCertificates = ref(0) // Certificados en procesamiento
const packageCertificates = ref(0) // Certificados en paquetes
// Función para calcular progreso simple
const calculateProgress = (certificates: any[]) => {
	const total = certificates.length
	const processed = certificates.filter(cert => cert.success || cert.error).length
	const processing = certificates.filter(cert => !cert.success && !cert.error && !cert.package).length
	const inPackages = certificates.filter(cert => cert.package).length

	totalCertificates.value = total
	processedCertificates.value = processed
	processingCertificates.value = processing
	packageCertificates.value = inPackages
}

// Load certificates
const loadCertificates = async (reset = false) => {
	if (reset) {
		certificatesList.value = []
	}

	try {
		// Check if user is authenticated
		if (!auth.currentUser) {
			throw new Error('User not authenticated')
		}

		// Check if institution ID is available
		if (!currentInstitution.value) {
			// Try to load institutions if not already loaded
			try {
				await store.dispatch('global/loadInstitutions', null, { root: true })

				// Check again if we have an institution ID
				if (!currentInstitution.value) {
					throw new Error('Institution ID not available')
				}
			} catch (error) {
				console.error('Error loading institutions:', error)
				throw new Error('Institution ID not available')
			}
		}

		// Limpiar listeners anteriores
		const certificatesRef = dbRef(db, getInstitutionPath('/certificates/queue'))
		off(certificatesRef)
		// Configurar nuevo listener
		onValue(certificatesRef, snapshot => {
			if (snapshot.exists()) {
				const newCertificates = Object.entries(snapshot.val()).map(([id, data]: [string, any]) => ({
					id,
					...data,
					selected: false
				}))
				// Filtrar certificados que ya están en un paquete
				const filteredCertificates = newCertificates.filter(certificate => !certificate.package)
				certificatesList.value = filteredCertificates

				// Calcular progreso simple
				calculateProgress(filteredCertificates)
			} else {
				certificatesList.value = []
				totalCertificates.value = 0
				processedCertificates.value = 0
				processingCertificates.value = 0
			}
		})
	} catch (error) {
		console.error('Error loading certificates:', error)
		if (error instanceof Error) {
			if (error.message === 'User not authenticated') {
				showErrorModal.value = true
				errorMessage.value = 'Por favor inicie sesión para continuar'
			} else if (error.message === 'Institution ID not available') {
				showErrorModal.value = true
				errorMessage.value = 'No se pudo determinar la institución. Por favor, seleccione una institución.'
			} else if (error.message.includes('Permission denied')) {
				showErrorModal.value = true
				errorMessage.value = 'No tiene permisos para acceder a los certificados. Por favor contacte al administrador.'
			} else {
				showErrorModal.value = true
				errorMessage.value = 'Error al cargar los certificados: ' + error.message
			}
		} else {
			showErrorModal.value = true
			errorMessage.value = 'Error al cargar los certificados'
		}
	}
}

// Load packages
const loadPackages = async (date: string) => {
	const packagesRef = dbRef(db, getInstitutionPath('/certificates/packages'))

	// +++ limpiar listener anterior (ref o query)
	if (_packagesListenerQuery) {
		off(_packagesListenerQuery)
		_packagesListenerQuery = null
	} else {
		off(packagesRef)
	}

	// +++ probe: si hay >= 26 paquetes, aplicamos filtro por año en servidor
	const probeSnapshot = await get(query(packagesRef, limitToFirst(26)))
	const probeValue = probeSnapshot.val() as Record<string, unknown> | null
	const useYearFilter = probeValue ? Object.keys(probeValue).length > 25 : false
	const { start, end } = getYearRange(date)

	let packagesQuery: Query = packagesRef
	if (useYearFilter) {
		packagesQuery = query(packagesRef, orderByChild('id'), startAt(start), endAt(end))
	}

	_packagesListenerQuery = packagesQuery

	// (onValue NO se cambia; solo se le pasa el query cuando aplica)
	onValue(packagesQuery, snapshot => {
		const data = snapshot.val()
		if (data) {
			const entries = Object.entries(data)
			const filteredEntries = useYearFilter
				? entries
				: entries.filter(([id, packageData]) => {
						const raw = (packageData as Record<string, unknown>).id ?? id
						const value = typeof raw === 'number' ? raw : Number(raw)
						return Number.isFinite(value) && value >= start && value <= end
					})

			packages.value = filteredEntries.map(([id, packageData]) => ({
				id: Number(id),
				certificates: (packageData as any).certificates || {},
				folioControl: (packageData as any).folioControl || {},
				numCertificates: (packageData as any).numCertificates || 0,
				sent: (packageData as any).sent || false,
				sentData: (packageData as any).sentData,
				success: (packageData as any).success,
				label: (packageData as any).label || '',
				sign: (packageData as any).sign || false,
				responsables: (packageData as any).responsables || {}
			}))
		} else {
			packages.value = []
		}
	})
}

const searchCertificates = () => {
	certificateService.searchCertificates(singleSelect.value.value as TSearchType, searchValue.value, currentInstitution.value, testMode.value).then(result => {
		searchResult.value = result
	})
}

const selectAllCertificates = () => {
	certificatesList.value.forEach(certificate => {
		certificate.selected = true
	})
}
const unselectAllCertificates = () => {
	certificatesList.value.forEach(certificate => {
		certificate.selected = false
	})
}
const removeSelectedCertificates = async () => {
	if (!confirm(`¿Está seguro de eliminar los ${selectedCertificatesList.value.length} certificados seleccionados?`)) return

	try {
		loading.value = true
		selectedCertificates.value = certificatesList.value.filter(certificate => certificate.selected)

		if (selectedCertificates.value.length === 0) {
			showErrorModal.value = true
			errorMessage.value = 'No hay certificados seleccionados para eliminar'
			return
		}

		// Crear objeto de actualización para eliminación por lotes
		const updates: Record<string, null> = {}
		for (const certificate of selectedCertificates.value) {
			const path = getInstitutionPath(`/certificates/queue/${certificate.id}`)
			updates[path] = null
		}

		// Eliminar todos los certificados en una sola operación
		await update(dbRef(db), updates)

		// Actualizar la lista local eliminando los certificados borrados
		const deletedIds = new Set(selectedCertificates.value.map(cert => cert.id))
		certificatesList.value = certificatesList.value.filter(certificate => !deletedIds.has(certificate.id))

		// Limpiar selección
		selectedCertificates.value = []
	} catch (error) {
		console.error('Error al eliminar certificados:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al eliminar los certificados. Por favor, intente nuevamente.'
		// En caso de error, recargar la lista para mantener consistencia
		await loadCertificates(true)
	} finally {
		loading.value = false
	}
}
const removeAllCertificatesWithErrors = async () => {
	const certificatesWithErrors = certificatesList.value.filter(
		certificate => certificate.error && certificate.error.code && certificate.error.message && certificate.error.error && certificate.error.error.code !== undefined && certificate.error.error.message
	)

	if (certificatesWithErrors.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'No hay certificados con errores para eliminar'
		return
	}
	if (!confirm(`¿Está seguro de eliminar ${certificatesWithErrors.length} certificados con errores?`)) return

	try {
		loading.value = true

		// Crear objeto de actualización para eliminación por lotes
		const updates: Record<string, null> = {}
		for (const certificate of certificatesWithErrors) {
			const path = getInstitutionPath(`/certificates/queue/${certificate.id}`)
			updates[path] = null
		}

		// Eliminar todos los certificados en una sola operación
		await update(dbRef(db), updates)

		// Actualizar la lista local eliminando los certificados borrados
		const deletedIds = new Set(certificatesWithErrors.map(cert => cert.id))
		certificatesList.value = certificatesList.value.filter(certificate => !deletedIds.has(certificate.id))
	} catch (error) {
		console.error('Error removing certificates:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al eliminar los certificados. Por favor, intente nuevamente.'
		// En caso de error, recargar la lista para mantener consistencia
		await loadCertificates(true)
	} finally {
		loading.value = false
	}
}

const createPackage = () => {
	selectedCertificates.value = certificatesList.value.filter(c => c.selected)
	if (selectedCertificates.value.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'Debe seleccionar al menos 1 registro'
		return
	}
	showPackageModal.value = true
}

const abrirNuevoCertificado = () => {
	showNewCertificateModal.value = true
}

// Watch for tab changes
watch(activeTab, async newTab => {
	if (newTab === 'certificates') {
		await loadCertificates(true)
	} else if (newTab === 'packages') {
		await loadPackages(formateDatePicker(packagesDate.value))
	}
})

// Watch for institution ID changes
watch(currentInstitution, async (newId, oldId) => {
	if (newId !== oldId) {
		// Reset state
		certificatesList.value = []

		// Load data based on active tab
		if (activeTab.value === 'certificates') {
			await loadCertificates(true)
		} else if (activeTab.value === 'packages') {
			await loadPackages(formateDatePicker(packagesDate.value))
		}
	}
})

watch(testMode, async (newMode, oldMode) => {
	if (newMode !== oldMode) {
		// Limpiar datos actuales
		certificatesList.value = []
		packages.value = []
		searchResult.value = []
		searchValue.value = ''

		// Recargar datos según la pestaña activa
		if (activeTab.value === 'certificates') {
			await loadCertificates(true)
		} else if (activeTab.value === 'packages') {
			await loadPackages(formateDatePicker(packagesDate.value))
		}
	}
})

watch(packagesDate, async (newDate, oldDate) => {
	if (newDate !== oldDate) {
		if (newDate !== null) {
			await loadPackages(formateDatePicker(newDate))
		}
	}
})
// Initialize
onMounted(async () => {
	// Make sure we have the institution ID before loading certificates
	if (!currentInstitution.value) {
		try {
			// Try to load institutions if not already loaded
			await store.dispatch('global/loadInstitutions', null, { root: true })

			// Check again if we have an institution ID
			if (!currentInstitution.value) {
				showErrorModal.value = true
				errorMessage.value = 'No se pudo determinar la institución. Por favor, seleccione una institución.'
				return
			}
		} catch (error) {
			console.error('Error loading institutions:', error)
			showErrorModal.value = true
			errorMessage.value = 'Error al cargar las instituciones. Por favor, intente nuevamente.'
			return
		}
	}

	await loadCertificates(true)

	// Inicializar listener de estado de subida
	cleanupUploadStatusListener = setupUploadStatusListener()
})

onUnmounted(() => {
	// Limpiar listeners al desmontar el componente
	const certificatesRef = dbRef(db, getInstitutionPath('/certificates/queue'))
	off(certificatesRef)

	if (_packagesListenerQuery) {
		off(_packagesListenerQuery)
		_packagesListenerQuery = null
	} else {
		const packagesRef = dbRef(db, getInstitutionPath('/certificates/packages'))
		off(packagesRef)
	}

	if (cleanupUploadStatusListener) {
		cleanupUploadStatusListener()
		cleanupUploadStatusListener = null
	}
})

// Watch for upload status changes with extended timeout for certificates
const PROCESSING_TIMEOUT_MS = 300000 // 5 minutos (alineado con timeoutSeconds del trigger de Storage)

const setupUploadStatusListener = () => {
	const user = auth.currentUser
	if (!user) {
		console.error('Usuario no autenticado, no se puede configurar el listener de subida.')
		return () => {
			console.log('Limpieza de listener de estado de subida.')
		}
	}

	const uploadStatusRef = dbRef(db, `users/${user.uid}/userData/uploadCertificate`)
	let unsubscribe: (() => void) | null = null
	let timeoutId: number | null = null
	let isProcessing = false

	const clearProcessingTimeout = () => {
		if (timeoutId) {
			clearTimeout(timeoutId)
			timeoutId = null
		}
	}

	const markTimeoutInFirebase = async () => {
		try {
			await update(uploadStatusRef, {
				status: 'timeout',
				errorsMessages: [
					{
						message: 'El procesamiento de certificados excedió el tiempo máximo de espera'
					}
				]
			})
		} catch (error) {
			console.error('Error al marcar timeout en Firebase:', error)
			showErrorModal.value = true
			errorMessage.value =
				'El procesamiento de certificados tardó más de 5 minutos. Esto puede deberse a un archivo muy grande o problemas de conexión. Por favor, intente con un archivo más pequeño o contacte al administrador.'
			store.dispatch('loader/setLoading', { isLoading: false })
			store.dispatch('loader/cancelUpload')
			isProcessing = false
		}
	}

	const scheduleProcessingTimeout = (initTime: number) => {
		clearProcessingTimeout()
		const now = Date.now()
		const elapsed = initTime ? now - initTime : 0
		const remaining = Math.max(0, PROCESSING_TIMEOUT_MS - elapsed)

		timeoutId = window.setTimeout(() => {
			if (!isProcessing) return
			console.error('Tiempo de espera excedido para certificados:', elapsed, 'ms')
			void markTimeoutInFirebase()
		}, remaining)
	}

	const listenerCallback = (snapshot: any) => {
		const uploadData = snapshot.val()
		if (uploadData === null) {
			store.dispatch('loader/setLoading', { isLoading: false })
			store.dispatch('loader/cancelUpload')
			clearProcessingTimeout()
			isProcessing = false
			return
		}

		const status = uploadData.status
		const errors = uploadData.errorsMessages
		const initTime = uploadData.initTime || 0

		switch (status) {
			case 'uploading': {
				isProcessing = true
				const now = Date.now()

				if (initTime && now - initTime > PROCESSING_TIMEOUT_MS) {
					console.error('Tiempo de espera excedido al iniciar:', now - initTime, 'ms')
					void markTimeoutInFirebase()
					break
				}

				scheduleProcessingTimeout(initTime)
				store.dispatch('loader/setLoading', {
					isLoading: true,
					message: 'Procesando certificados... (puede tardar varios minutos para archivos grandes)'
				})
				break
			}

			case 'complete':
				isProcessing = false
				clearProcessingTimeout()
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				break

			case 'error': {
				isProcessing = false
				clearProcessingTimeout()

				let formattedErrors
				if (Array.isArray(errors)) {
					formattedErrors = errors.map(error => {
						if (typeof error === 'object' && error !== null) {
							return {
								row: error.row || '',
								message: error.message || '',
								data: error.data || null
							}
						}
						return {
							message: error.toString()
						}
					})
				} else if (typeof errors === 'object' && errors !== null) {
					formattedErrors = [
						{
							message: errors.message || JSON.stringify(errors)
						}
					]
				} else {
					formattedErrors = [
						{
							message: errors?.toString() || 'Error desconocido en la carga del archivo'
						}
					]
				}
				store.dispatch('notification/showError', formattedErrors)
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				errorCount.value = formattedErrors.length
				showCleanErrorModal.value = true
				break
			}

			case 'timeout':
				isProcessing = false
				clearProcessingTimeout()
				showErrorModal.value = true
				errorMessage.value =
					'El procesamiento de certificados tardó más de 5 minutos. Esto puede deberse a un archivo muy grande o problemas de conexión. Por favor, intente con un archivo más pequeño o contacte al administrador.'
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				break

			default:
				console.log('Listener: Estado desconocido o sin acción: ', status, '. Ocultando loader.')
				isProcessing = false
				clearProcessingTimeout()
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				break
		}
	}

	const errorCallback = (error: Error) => {
		console.error('Error en el listener de estado de subida:', error)
		isProcessing = false
		clearProcessingTimeout()
		store.dispatch('loader/setLoading', { isLoading: false })
		store.dispatch('loader/cancelUpload')
	}

	unsubscribe = onValue(uploadStatusRef, listenerCallback, errorCallback)

	return () => {
		if (unsubscribe) {
			unsubscribe()
			unsubscribe = null
		}
		clearProcessingTimeout()
		isProcessing = false
	}
}

const confirmCleanErrors = async () => {
	await store.dispatch('cleanUploadCertificates').then(() => {
		showCleanErrorModal.value = false
		loadCertificates(true)
	})
}

const handleRefreshPackages = async () => {
	await loadPackages(formateDatePicker(packagesDate.value))
}
</script>
<template>
	<VaCard square class="ml-20" bordered>
		<VaCardTitle>
			<div class="certificates-page-header">
				<p class="certificates-page-header__title">Generación y Firmado de Certificados</p>
				<div class="certificates-page-header__actions">
					<VaButton class="certificates-page-header__action" color="primary" icon="download" @click="showModalDownloadTemplate = !showModalDownloadTemplate">Descargar Plantilla</VaButton>
					<div class="certificates-page-header__action certificates-page-header__uploader">
						<FileUploader
							@upload-start="handleUploadStart"
							@upload-complete="handleUploadComplete"
							@upload-error="handleUploadError"
							@upload-progress="handleUploadProgress"
							buttonText="Subir Excel"
							acceptTypes=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
							:disabled="false"
							buttonClass="btn btn-success"
							iconClass="fas fa-upload"
							:storagePath="`files/institutions/${currentInstitution}/${auth.currentUser?.uid}/certificates`"
							:file-name="'fileMassiveCertificates.xlsx'"
							:institution-id="currentInstitution"
							type="certificates"
							:test-mode="testMode" />
					</div>
					<VaButton class="certificates-page-header__action" color="secondary" icon="add" @click="abrirNuevoCertificado">Nuevo Certificado</VaButton>
				</div>
			</div>
		</VaCardTitle>
		<VaCardContent>
			<div class="row">
				<div class="col-md-4">
					<VaTabs v-model="activeTab" color="primary">
						<template #tabs>
							<VaTab :class="[{ active: activeTab === 'certificates' }]" icon="task" label="Certificados" name="certificates" @click="selectTab('certificates')" />
							<VaTab :class="[{ active: activeTab === 'packages' }]" icon="inventory_2" label="Paquetes" name="packages" @click="selectTab('packages')" />
							<VaTab :class="[{ active: activeTab === 'search' }]" icon="search" label="Búsqueda" name="search" @click="selectTab('search')" />
						</template>
					</VaTabs>
				</div>
				<div class="col-md-8">
					<!-- Contador de Certificados -->
					<div v-if="certificatesList.length > 0">
						<div class="counter-card">
							<div class="counter-item error">
								<VaIcon name="warning" class="counter-icon error" />
								<div class="counter-content">
									<span style="color: red; font-size: 10px">El limite por plantilla es de 300 certificados</span>
								</div>
							</div>
							<div class="counter-item">
								<VaIcon name="school" class="counter-icon total" />
								<div class="counter-content">
									<span class="counter-number">{{ certificatesList.length }}</span>
									<span class="counter-label">Total de Certificados</span>
								</div>
							</div>
							<div class="counter-item">
								<VaIcon name="check_circle" class="counter-icon selected" />
								<div class="counter-content">
									<span class="counter-number">{{ selectedCertificatesList.length }}</span>
									<span class="counter-label">Seleccionados</span>
								</div>
							</div>
							<div class="counter-item">
								<VaIcon name="error" class="counter-icon error" />
								<div class="counter-content">
									<span class="counter-number">{{ certificatesList.filter(c => c.error).length }}</span>
									<span class="counter-label">Con Errores</span>
								</div>
							</div>
							<div class="counter-item">
								<VaIcon name="pending" class="counter-icon pending" />
								<div class="counter-content">
									<span class="counter-number">{{ certificatesList.filter(c => c.warning).length }}</span>
									<span class="counter-label">Advertencias</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<hr class="divider" style="color: #0d6efd" />
			<div class="content">
				<div class="row justify-content-end mb-3">
					<div class="col-md-12" v-if="certificatesList.length > 0 && activeTab === 'certificates'">
						<div class="action-buttons-container">
							<VaButton color="success" icon="compress" @click="createPackage">Empaquetar Seleccionados</VaButton>
							<VaButton color="warning" icon="cancel" @click="unselectAllCertificates">Quitar Seleccionados</VaButton>
							<VaButton color="primary" icon="add" @click="selectAllCertificates">Seleccionar Todos</VaButton>
							<VaButton color="danger" icon="delete" @click="removeSelectedCertificates">Eliminar Seleccionados</VaButton>
							<VaPopover message="Este proceso eliminará todos los certificados con errores sin necesidad de seleccionarlos">
								<VaButton color="danger" icon="delete" @click="removeAllCertificatesWithErrors">Eliminar Todos (Certificados con errores)</VaButton>
							</VaPopover>
						</div>
					</div>
				</div>
				<div v-if="activeTab === 'certificates'">
					<div v-if="certificatesList.length === 0" class="empty-state">
						<img src="../assets/icons/certificateIcon.svg" alt="No certificates" class="empty-icon" />
						<p class="empty-title">Aún no has creado certificados</p>
						<p class="empty-description">Crea un nuevo certificado o importa varios desde un archivo de excel</p>
					</div>
					<div v-else>
						<!-- Contador de progreso simple -->
						<div v-if="totalCertificates > 0 && processedCertificates !== totalCertificates" class="progress-counter">
							<div class="progress-info">
								<span class="progress-text"> 📊 Progreso: {{ processedCertificates }}/{{ totalCertificates }} certificados procesados </span>
								<span v-if="processingCertificates > 0" class="processing-text"> 🔄 {{ processingCertificates }} en proceso </span>
							</div>
							<div class="progress-details">
								<span v-if="packageCertificates > 0" class="package-text"> 📦 {{ packageCertificates }} en paquetes </span>
							</div>
							<div class="progress-bar">
								<div class="progress-fill" :style="{ width: totalCertificates > 0 ? (processedCertificates / totalCertificates) * 100 + '%' : '0%' }"></div>
							</div>
						</div>

						<CertificatesView :certificates="certificatesList" :currentInstitution="currentInstitution" :testMode="testMode" :institution="store.state.global.selectedInstitution" />
					</div>
				</div>
				<div v-if="activeTab === 'packages'" class="page-container">
					<div v-if="packages.length === 0" class="empty-state">
						<div class="packages-year-filter" style="width: 20%">
							<span class="packages-year-label">Filtrar por año</span>
							<VueDatePicker v-model="packagesDate" :formats="{ input: 'yyyy' }" :locale="es" year-picker auto-apply class="packages-year-picker" :year-range="[2018, 2100]" />
							<VaPopover message="Hasta 25 paquetes: se muestran todos. Más de 25: se aplica filtro por año." placement="right">
								<button type="button" class="packages-year-info" aria-label="Información del filtro">
									<VaIcon name="info" size="small" />
								</button>
							</VaPopover>
						</div>
						<img src="../assets/icons/certificate.svg" alt="No packages" class="empty-icon" />
						<p class="empty-title">Aún no has creado paquetes</p>
						<p class="empty-description">Crea un nuevo paquete o importa varios desde un archivo de excel</p>
					</div>
					<div v-else class="packages-content">
						<div class="packages-year-filter">
							<span class="packages-year-label">Filtrar por año</span>
							<VueDatePicker v-model="packagesDate" :formats="{ input: 'yyyy' }" :locale="es" year-picker auto-apply class="packages-year-picker" :year-range="[2018, 2100]" />
							<VaPopover message="Hasta 25 paquetes: se muestran todos. Más de 25: se aplica filtro por año." placement="right">
								<button type="button" class="packages-year-info" aria-label="Información del filtro">
									<VaIcon name="info" size="small" />
								</button>
							</VaPopover>
						</div>
						<PackageViewCertificate
							:packages="packages"
							:institutionId="currentInstitution"
							:testMode="testMode"
							:currentInstitution="currentInstitution"
							@package-removed="handleRefreshPackages"
							@refresh-packages="handleRefreshPackages" />
					</div>
				</div>
				<div v-if="activeTab === 'search'" class="search-section">
					<!-- Formulario de Búsqueda -->
					<div class="search-form">
						<div class="search-controls">
							<div class="search-select">
								<label for="searchType" class="search-label">Buscar certificado por:</label>
								<select id="searchType" v-model="singleSelect.value" class="form-control search-select-input">
									<option value="folio">Folio de Control</option>
									<option value="numero">Número de Control</option>
									<option value="curp">CURP</option>
								</select>
							</div>
							<div class="search-input-group">
								<input type="text" v-model="searchValue" :placeholder="`Ingresa el ${singleSelect.text.toLowerCase()}`" @keyup.enter="searchCertificates" class="form-control search-input" />
								<button class="btn btn-primary search-button" @click="searchCertificates"><i class="fas fa-search"></i> Buscar</button>
							</div>
						</div>
					</div>

					<!-- Resultados de Búsqueda -->
					<div v-if="searchResult.length > 0" class="search-results">
						<div class="results-header">
							<VaIcon name="list_alt" class="results-icon" />
							<span class="results-title">Resultados de Búsqueda ({{ searchResult.length }})</span>
						</div>
						<div class="results-grid">
							<div v-for="certificate in searchResult" :key="certificate.id" class="certificate-details">
								<!-- Header del Certificado -->
								<div class="certificate-header">
									<div class="header-content">
										<div class="student-info">
											<div class="student-avatar">
												<VaIcon name="person" class="student-icon" />
											</div>
											<div class="student-details">
												<h3 class="student-name">
													{{
														`${certificate?.data.Alumno.nombre ? certificate?.data.Alumno.nombre.toUpperCase() : ''} ${certificate?.data.Alumno.primerApellido ? certificate?.data.Alumno.primerApellido.toUpperCase() : ''} ${certificate?.data.Alumno.segundoApellido ? certificate?.data.Alumno.segundoApellido.toUpperCase() : ''}`
													}}
												</h3>
												<p class="student-career">{{ certificate?.data.Carrera?.nombreCarrera || 'Carrera no especificada' }}</p>
											</div>
										</div>
										<div class="certificate-actions">
											<VaPopover message="Ver detalles completos del certificado">
												<VaButton
													round
													icon="visibility"
													color="#0056b3"
													type="button"
													@click="openCertificateDetail(certificate)"
													style="display: flex; align-items: center; justify-content: center" />
											</VaPopover>
										</div>
									</div>
								</div>

								<!-- Contenido del Certificado -->
								<div class="certificate-content">
									<div class="certificate-info">
										<div class="info-item">
											<VaIcon name="fingerprint" class="info-icon" />
											<span class="info-label">Folio control:</span>
											<span class="info-value">{{ certificate?.folioControl || 'No disponible' }}</span>
										</div>
										<div class="info-item">
											<VaIcon name="badge" class="info-icon" />
											<span class="info-label">Núm. control:</span>
											<span class="info-value">{{ certificate?.data.numeroControl || 'No disponible' }}</span>
										</div>
										<div class="info-item">
											<VaIcon name="badge" class="info-icon" />
											<span class="info-label">CURP:</span>
											<span class="info-value">{{ certificate?.data.Alumno.curp || 'No disponible' }}</span>
										</div>
										<div v-if="certificate.packageLabel" class="info-item">
											<VaIcon name="collections_bookmark" class="info-icon" />
											<span class="info-label">Paquete:</span>
											<span class="info-value">{{ certificate.packageLabel }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Estado Inicial -->
					<div v-else-if="searchResult.length === 0" class="initial-state">
						<div class="empty-state">
							<VaIcon name="search" class="empty-icon" />
							<p class="empty-title">Busca Certificados</p>
							<p class="empty-description">Selecciona un criterio de búsqueda e ingresa el valor correspondiente</p>
						</div>
					</div>

					<!-- Sin Resultados -->
					<div v-else class="empty-state">
						<p class="empty-title">No se encontraron resultados</p>
						<p class="empty-description">Intente con otro criterio de búsqueda</p>
					</div>
					<!-- Modal único de detalles: muestra el certificado seleccionado -->
					<CertificateDetailsModal
						v-model="showCertificate"
						:certificate="selectedCertificateForDetail"
						:isTestMode="testMode"
						:currentInstitution="currentInstitution"
						@update:modelValue="onCertificateDetailModalUpdate" />
				</div>
			</div>
		</VaCardContent>
	</VaCard>
	<CertificatesModal v-model="showModalDownloadTemplate" title="Plantilla de Certificados" :carreras="carreras" :institutionId="currentInstitution" />
	<NewCertificateModal v-model="showNewCertificateModal" :institution="store.state.global.selectedInstitution" :isTestMode="testMode" :currentInstitution="currentInstitution" :isEdit="false" />
	<PackageModal v-model="showPackageModal" :selectedCertificates="selectedCertificates" :isTestMode="testMode" :institutionId="currentInstitution" @update:modelValue="showPackageModal = $event" />
	<CleanErrorModal v-model="showCleanErrorModal" :test-mode="testMode" :error-count="errorCount" @confirm-clean="confirmCleanErrors" />
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
/* Filtro por año: diseño responsive (adaptable a pantallas pequeñas) */
.packages-year-filter {
	display: inline-flex;
	width: auto;
	min-width: min(100%, 280px);
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 0.875rem;
	background: linear-gradient(135deg, var(--va-primary) 0%, color-mix(in srgb, var(--va-primary) 85%, white) 100%);
	border-radius: 9px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	margin-bottom: 1rem;
}

.packages-year-label {
	font-size: 1rem;
	font-weight: 600;
	color: white;
	letter-spacing: 0.02em;
	white-space: nowrap;
}

.packages-year-picker {
	--dp-input-padding: 0.35rem 0.6rem;
	--dp-font-size: 0.875rem;
}

.packages-year-picker :deep(.dp__input) {
	min-width: 4rem;
	border-radius: 999px;
	border: none;
	background: rgba(255, 255, 255, 0.95);
	font-weight: 500;
}

.packages-year-info {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 1.5rem;
	height: 1.5rem;
	min-width: 1.5rem;
	min-height: 1.5rem;
	padding: 0;
	border: none;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.25);
	color: white;
	cursor: pointer;
	transition:
		background 0.2s,
		transform 0.15s;
}

.packages-year-info:hover {
	background: rgba(255, 255, 255, 0.4);
	transform: scale(1.05);
}

/* Responsive: pantallas pequeñas (móviles y tablets) */
@media (max-width: 768px) {
	.packages-year-filter {
		width: 100%;
		min-width: 0;
		gap: 0.75rem;
		padding: 0.75rem 1rem;
	}

	.packages-year-label {
		font-size: 1.05rem;
	}

	.packages-year-picker {
		--dp-input-padding: 0.5rem 0.75rem;
		--dp-font-size: 1rem;
	}

	.packages-year-picker :deep(.dp__input) {
		min-width: 4.5rem;
	}

	.packages-year-info {
		width: 2rem;
		height: 2rem;
		min-width: 2rem;
		min-height: 2rem;
	}
}

@media (max-width: 480px) {
	.packages-year-filter {
		padding: 0.875rem 1rem;
		gap: 0.625rem;
	}

	.packages-year-label {
		font-size: 1rem;
	}
}

.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.certificates-page-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	flex-wrap: wrap;
	padding: 1rem;
	width: 100%;
}

.certificates-page-header__title {
	margin: 0;
	font-size: 1.5rem;
	font-weight: 700;
	line-height: 1.2;
	flex: 1 1 260px;
	min-width: min(100%, 260px);
}

.certificates-page-header__actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 0.5rem;
	flex: 0 1 auto;
	min-width: min(100%, 320px);
	margin-left: auto;
}

.certificates-page-header__action {
	flex: 0 0 auto;
}

.certificates-page-header__uploader {
	display: inline-flex;
}

.certificates-page-header__uploader :deep(.btn) {
	white-space: nowrap;
}

@media (max-width: 768px) {
	.certificates-page-header {
		align-items: flex-start;
	}
	.certificates-page-header__actions {
		justify-content: flex-start;
		width: 100%;
	}
}

@media (max-width: 576px) {
	.certificates-page-header__action {
		width: 100%;
	}
	.certificates-page-header__actions :deep(.btn),
	.certificates-page-header__actions :deep(.va-button) {
		width: 100%;
	}
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.content {
	background: white;
	border-radius: 8px;
	padding: 20px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-state {
	text-align: center;
	padding: 40px;
	color: #666;
}

/* Estilos para contador de progreso simple */
.progress-counter {
	background: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 20px;
}

.progress-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.progress-text {
	font-weight: 500;
	color: #495057;
	font-size: 16px;
}

.processing-text {
	font-weight: 500;
	color: #007bff;
	font-size: 14px;
}

.progress-details {
	display: flex;
	gap: 16px;
	margin-bottom: 8px;
	font-size: 12px;
}

.package-text {
	color: #6c757d;
	font-weight: 500;
}

.progress-bar {
	width: 100%;
	height: 8px;
	background: #e9ecef;
	border-radius: 4px;
	overflow: hidden;
}

.progress-fill {
	height: 100%;
	background: linear-gradient(90deg, #007bff, #28a745);
	border-radius: 4px;
	transition: width 0.3s ease;
}

.packages-content {
	flex: 1;
	overflow: visible;
	min-height: 0;
	padding-bottom: 2rem;
	margin-bottom: 40px;
}
/* Estilos para habilitar scroll en el contenedor principal */
:deep(.main-content) {
	overflow-y: auto !important;
	height: calc(100vh - 60px) !important;
	padding: 1rem !important;
}

/* Asegurar que el contenido tenga scroll */
:deep(.va-card) {
	max-height: none;
	overflow: visible;
}

/* Asegurar que las tablas tengan scroll horizontal si es necesario */
:deep(.va-data-table) {
	overflow-x: auto;
}

/* Estilos para el scroll personalizado */
:deep(.main-content)::-webkit-scrollbar {
	width: 8px;
}

:deep(.main-content)::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

:deep(.main-content)::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

:deep(.main-content)::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* ===== ESTILOS PARA SECCIÓN DE BÚSQUEDA ===== */

/* Contenedor Principal de Búsqueda */
.search-section {
	padding: 1.5rem;
	background: white;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
	margin-bottom: 40px;
}

/* Formulario de Búsqueda */
.search-form {
	margin-bottom: 2rem;
}

.search-controls {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	max-width: 600px;
	margin: 0 auto;
}

.search-select {
	width: 100%;
}

.search-label {
	display: block;
	margin-bottom: 0.5rem;
	font-weight: 500;
	color: #1a2a3a;
	font-size: 0.875rem;
}

.search-select-input {
	width: 100%;
	padding: 0.75rem;
	border: 2px solid #e9ecef;
	border-radius: 8px;
	font-size: 0.875rem;
	transition: all 0.3s ease;
}

.search-select-input:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
	outline: none;
}

.search-input-group {
	display: flex;
	gap: 1rem;
	align-items: flex-end;
}

.search-input {
	flex: 1;
	padding: 0.75rem;
	border: 2px solid #e9ecef;
	border-radius: 8px;
	font-size: 0.875rem;
	transition: all 0.3s ease;
}

.search-input:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
	outline: none;
}

.search-button {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
	transition: all 0.3s ease;
	box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
	border: none;
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	font-weight: 500;
}

.search-button:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

/* Resultados de Búsqueda */
.search-results {
	margin-top: 2rem;
}

.results-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
}

.results-icon {
	color: #007bff;
	font-size: 1.25rem;
}

.results-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
}

.results-grid {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

/* Estados Vacíos */
.initial-state {
	margin-top: 2rem;
}

.empty-icon {
	font-size: 4rem;
	color: #6c757d;
	margin-bottom: 1rem;
}

.empty-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 0.5rem;
}

.empty-description {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive Design para Búsqueda */
@media (max-width: 768px) {
	.search-section {
		padding: 1rem;
	}

	.search-controls {
		gap: 1rem;
	}

	.search-input-group {
		flex-direction: column;
		align-items: stretch;
	}

	.search-button {
		width: 100%;
	}

	.results-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.results-title {
		font-size: 1.125rem;
	}

	.empty-icon {
		font-size: 3rem;
	}

	.empty-title {
		font-size: 1.25rem;
	}
}

/* ===== ESTILOS PARA CONTADOR DE CERTIFICADOS ===== */

.counter-card {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 1rem;
	align-items: stretch;
}

.counter-item {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.25rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	border-left: 4px solid #007bff;
	transition: all 0.3s ease;
}
.counter-item.error {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.25rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	border-left: 4px solid #dc3545;
	transition: all 0.3s ease;
	grid-column: 1 / -1; /* El aviso debe ocupar toda la fila en cualquier tamaño */
}

.counter-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.counter-icon {
	font-size: 2rem;
	flex-shrink: 0;
}

.counter-icon.total {
	color: #007bff;
}

.counter-icon.selected {
	color: #28a745;
}

.counter-icon.error {
	color: #dc3545;
}

.counter-icon.pending {
	color: #ffc107;
}

.counter-content {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 0;
}

.counter-number {
	font-size: 12px;
	font-weight: 700;
	color: #1a2a3a;
	line-height: 1;
}

.counter-label {
	font-size: 10px;
	color: #6c757d;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	white-space: normal;
	word-break: break-word;
}

/* Responsive Design para Contador */
@media (max-width: 768px) {
	.counter-card {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding: 1rem;
		gap: 0.75rem;
	}

	.counter-item {
		padding: 0.75rem;
		gap: 0.75rem;
	}

	.counter-icon {
		font-size: 1.4rem;
	}

	.counter-number {
		font-size: 12px;
	}

	.counter-label {
		font-size: 0.75rem;
	}
}

@media (max-width: 480px) {
	.counter-card {
		grid-template-columns: 1fr;
	}

	.counter-item {
		justify-content: center;
		text-align: center;
	}

	.counter-item.error {
		justify-content: flex-start;
		text-align: left;
	}
}

/* ===== ESTILOS RESPONSIVOS PARA BOTONES DE ACCIÓN ===== */

.action-buttons-container {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: center;
	flex: 0 1 auto;
	min-width: fit-content;
}

/* Responsive para tablets */
@media (max-width: 1024px) {
	.action-buttons-container {
		gap: 0.5rem;
		font-size: 0.875rem;
		padding: 0.5rem 0.75rem;
	}
}

/* Responsive para móviles */
@media (max-width: 768px) {
	.action-buttons-container {
		flex-direction: column;
		align-items: stretch;
		gap: 0.5rem;
		width: 100%;
		justify-content: center;
		font-size: 0.875rem;
		padding: 0.75rem 1rem;
	}
}

/* Responsive para móviles pequeños */
@media (max-width: 480px) {
	.action-buttons-container {
		gap: 0.375rem;
		font-size: 0.8rem;
		padding: 0.625rem 0.875rem;
	}
}
/* ===== ESTILOS PARA CERTIFICATE DETAILS ===== */

/* Contenedor Principal */
.certificate-details {
	margin-bottom: 1rem;
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
	overflow: hidden;
}

.certificate-details:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Header del Certificado */
.certificate-header {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	padding: 1.5rem;
}

.header-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.student-info {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.student-avatar {
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	color: white;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.student-icon {
	font-size: 1.25rem;
	color: white;
}

.student-details {
	color: white;
}

.student-name {
	font-size: 1.25rem;
	font-weight: 600;
	margin: 0 0 0.25rem 0;
	text-transform: capitalize;
}

.student-career {
	font-size: 0.875rem;
	margin: 0;
	opacity: 0.9;
}

.certificate-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

/* Contenido del Certificado */
.certificate-content {
	padding: 1.5rem;
}

.certificate-info {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.info-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem;
	background: #f8f9fa;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.info-icon {
	color: #007bff;
	font-size: 1.125rem;
	flex-shrink: 0;
}

.info-label {
	font-weight: 600;
	color: #1a2a3a;
	min-width: 120px;
}

.info-value {
	color: #6c757d;
	flex: 1;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive Design */
@media (max-width: 768px) {
	.header-content {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.student-info {
		width: 100%;
	}

	.certificate-actions {
		width: 100%;
		justify-content: flex-end;
	}

	.info-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.info-label {
		min-width: auto;
	}

	.student-avatar {
		width: 40px;
		height: 40px;
	}

	.student-name {
		font-size: 1.125rem;
	}
}
</style>
